/**
 * AuthModal Component
 *
 * Diamond Architecture: Modal dialog for authentication.
 * Uses shadcn Dialog + the per-provider controls the ROSTER names.
 *
 * #7410 — which providers this modal offers, and in what order, is no longer
 * written here. `SIGN_IN_PROVIDERS` is generated from the
 * `variants[*].sign_in:` rows of `api/manifests/proto-enums/` (the same
 * rows that produce the iOS and Android rosters), and `PROVIDER_CONTROLS` is
 * pinned with `satisfies Record<SignInProvider, …>` — so a provider added to
 * the roster with no component here is a TYPE error rather than a button that
 * never appears. Before it, this file hand-listed "OTP, then a divider, then
 * Google if a client id was passed", which is why "the product does not offer
 * Apple on the web" and "nobody built Apple" were the same silence.
 *
 * Auto-closes on route change (Unicorn Standard):
 * - Clicking Terms/Privacy links closes modal
 * - Back button closes modal
 * - Any navigation closes modal
 */
import { AppDivider } from '@/shared/components/app';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { useAuthGate } from '@/shared/providers/AuthProvider';
import type { UserProfile } from '@letsgethome/lib/auth/types';
import {
  SIGN_IN_PROVIDERS,
  type SignInProvider,
} from '@letsgethome/lib/proto-enums/people/sign_in_roster_gen';
import type { GoogleClientId } from '@letsgethome/types/branded';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@letsgethome/ui/dialog';
import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import { GoogleSignIn } from './GoogleSignIn';
import { OtpFlow } from './OtpFlow';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  googleClientId?: GoogleClientId;
}

/** What one roster entry needs in order to render itself. */
interface ProviderContext {
  googleClientId?: GoogleClientId;
  /**
   * `AuthProvider.onAuthSuccess`, spelled with ITS signature rather than a
   * convenient `() => void`. It takes the signed-in `UserProfile` and it is
   * ASYNC, and a context that narrowed either would be lying to every control
   * it hands the callback to: the `user` argument would be dropped on the
   * floor, and `no-misused-promises` would (correctly) refuse the assignment
   * of a promise-returning function into a void-returning slot.
   */
  onAuthSuccess: (user?: UserProfile) => Promise<void>;
}

/**
 * One control per provider the roster may name.
 *
 * `satisfies Record<SignInProvider, …>` is the load-bearing half: the union is
 * DERIVED from the manifest, so this object cannot compile while a roster
 * entry has no control — and cannot carry a control for a provider the
 * manifest does not offer on this surface either.
 *
 * A control may still decline to render (Google returns `null` without a
 * client id, exactly as before). That is a BUILD-CONFIG question and a
 * different one from "does the product offer this provider", which is the
 * manifest's; conflating the two is the defect this roster closes.
 */
const PROVIDER_CONTROLS = {
  phone: () => <OtpFlow />,
  google: ({ googleClientId, onAuthSuccess }: ProviderContext) =>
    googleClientId ? <GoogleSignIn clientId={googleClientId} onSuccess={onAuthSuccess} /> : null,
} satisfies Record<SignInProvider, (context: ProviderContext) => ReactNode>;

export function AuthModal({ isOpen, onClose, googleClientId }: AuthModalProps) {
  const tModal = useTranslations('auth.modal');
  const { onAuthSuccess } = useAuthGate();
  const { pathname } = useLocation();
  const initialPathRef = useRef(pathname);

  useEffect(() => {
    if (isOpen && pathname !== initialPathRef.current) {
      onClose();
    }
  }, [pathname, isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      initialPathRef.current = pathname;
    }
  }, [isOpen, pathname]);

  const context: ProviderContext = { googleClientId, onAuthSuccess };
  // Render first, THEN place the separators: a control that declines to render
  // (Google with no client id) must not leave a dangling "or" behind it.
  const controls = SIGN_IN_PROVIDERS.map((provider) => ({
    provider,
    node: PROVIDER_CONTROLS[provider](context),
  })).filter((entry) => entry.node !== null);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">{tModal('title')}</DialogTitle>
          <DialogDescription className="text-center">{tModal('subtitle')}</DialogDescription>
        </DialogHeader>

        {controls.map(({ provider, node }, index) => (
          <Fragment key={provider}>
            {index > 0 && <AppDivider text={tModal('or')} />}
            <div className={index === 0 ? 'mt-4' : 'w-full'}>{node}</div>
          </Fragment>
        ))}
      </DialogContent>
    </Dialog>
  );
}
